import { z } from 'zod';

/**
 * Scheduling type constants (string literals).
 */
export const SCHEDULING_TYPE = {
  ACTUALS_ONLY: 'SCHEDULING_TYPE_ACTUALS_ONLY',
  PLANNED_AND_ACTUALS: 'SCHEDULING_TYPE_PLANNED_AND_ACTUALS',
} as const;

/**
 * Dependency type constants (string literals).
 */
export const DEPENDENCY_TYPE = {
  SUCCESSOR: 'DEPENDENCY_TYPE_SUCCESSOR',
  PREDECESSOR: 'DEPENDENCY_TYPE_PREDECESSOR',
} as const;

/**
 * Zod schema for scheduling type enum.
 */
export const schedulingTypeSchema = z.enum(SCHEDULING_TYPE);

/**
 * Zod schema for dependency type enum.
 */
export const dependencyTypeSchema = z.enum(DEPENDENCY_TYPE).nullable();

/**
 * Zod schema for item dependency.
 *
 * Represents a dependency edge between items.
 */
export const itemDependencySchema = z.object({
  dependencyItemId: z.number().int().positive(),
  dependencyType: dependencyTypeSchema,
});

/**
 * Zod schema for item domain model.
 *
 * This represents the domain model of an item with all its properties.
 * Timestamps are in milliseconds.
 */
export const itemSchema = z.object({
  itemId: z.number().int().positive(),
  name: z.string().optional(),
  itemTypeId: z.number().int().positive(),
  itemStatusId: z.number().int().positive(),
  priority: z.string(),
  parentId: z.number().int().positive().optional(),
  teamId: z.number().int().positive().optional(),
  estimationId: z.number().int().positive().optional(),
  planId: z.number().int().positive().optional(),
  timeSpentSeconds: z.number().int().optional(),
  schedulingType: schedulingTypeSchema.optional(),
  description: z.string().optional(),
  targetStartDate: z.number().int().optional(),
  targetFinishDate: z.number().int().optional(),
  plannedStartDate: z.number().int().optional(),
  plannedFinishDate: z.number().int().optional(),
  doneDate: z.number().int().optional(),
  dependencies: z.array(itemDependencySchema),
  createdAt: z.number().int().optional(),
  updatedAt: z.number().int().optional(),
  createdBy: z.string(),
  updatedBy: z.string(),
  externalKey: z.string().nullable(),
  externalLink: z.string().nullable(),
  externalCreatedAt: z.number().int().optional(),
});

/**
 * Zod schema for lightweight backlog item data used in backlog selection.
 *
 * Timestamps are in milliseconds.
 */
export const backlogItemDataSchema = z.object({
  itemId: z.number().int().positive(),
  priority: z.string(),
  parentId: z.number().int().positive().optional(),
  teamId: z.number().int().positive().optional(),
  estimationId: z.number().int().positive().optional(),
  itemTypeId: z.number().int().positive(),
  itemStatusId: z.number().int().positive(),
  targetStartDateTime: z.number().int().optional(),
  targetFinishDateTime: z.number().int().optional(),
  plannedStartDateTime: z.number().int().optional(),
  plannedFinishDateTime: z.number().int().optional(),
});

/**
 * Zod schema for streamed item entries.
 *
 * Extends full item data with optional globalPriority field.
 */
export const itemStreamEntrySchema = itemSchema.extend({
  globalPriority: z.string().optional(),
});

export type SchedulingType = z.infer<typeof schedulingTypeSchema>;
export type DependencyType = z.infer<typeof dependencyTypeSchema>;
export type ItemDependencyDTO = z.infer<typeof itemDependencySchema>;
export type ItemDTO = z.infer<typeof itemSchema>;
export type BacklogItemDataDTO = z.infer<typeof backlogItemDataSchema>;
export type ItemStreamEntryDTO = z.infer<typeof itemStreamEntrySchema>;
